// src/api/axios.js

import axios from 'axios';
import { logoutUser } from '../slices/userSlice'; // Adjust the import path as needed
import jwService from '../services/jwService';

// This is a placeholder for your Redux store. We will pass the real store in later.
let store;
let refreshPromise = null;

export const injectStore = (_store) => {
	store = _store;
};

// Create a new Axios instance
const api = axios.create({
	baseURL: process.env.REACT_APP_API_URL,
});

// This interceptor runs BEFORE each request is sent
api.interceptors.request.use(
	(config) => {
		const rawCookieToken = jwService.getToken();
		const cookieToken =
			typeof rawCookieToken === 'string' && rawCookieToken.split('.').length === 3
				? rawCookieToken
				: null;
		const token = localStorage.getItem('token') || cookieToken;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

// This interceptor runs AFTER a response is received
api.interceptors.response.use(
	(response) => response, // Simply return successful responses
	async (error) => {
		const originalRequest = error?.config;
		const status = error?.response?.status;

		// If the error is 401 and we haven't already tried to refresh
		if (status === 401 && originalRequest && !originalRequest._retry) {
			// Never try to refresh while already refreshing (or for the refresh endpoint itself)
			if (originalRequest?.url?.includes('/Accounts/Refresh')) {
				if (store) {
					store.dispatch(logoutUser());
				}
				jwService.cleanToken();
				return Promise.reject(error);
			}

			originalRequest._retry = true; // Mark that we've tried to refresh

			try {
				const refreshToken = localStorage.getItem('refreshToken');
				if (!refreshToken) {
					// If no refresh token, logout the user (do not hard-reload the app)
					if (store) {
						store.dispatch(logoutUser());
					} else {
						localStorage.removeItem('token');
						localStorage.removeItem('refreshToken');
						jwService.cleanToken();
					}
					return Promise.reject(new Error('No refresh token available'));
				}

				// Make (or await) the refresh token request (deduped)
				if (!refreshPromise) {
					refreshPromise = api
						.post('/Accounts/Refresh', { refreshToken })
						.then((response) => response.data)
						.finally(() => {
							refreshPromise = null;
						});
				}

				const data = await refreshPromise;

				// Store the new tokens
				localStorage.setItem('token', data.accessToken);
				if (data.refreshToken) {
					localStorage.setItem('refreshToken', data.refreshToken);
				}

				// Update the authorization header for the original request and retry it
				originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
				return api(originalRequest);
			} catch (refreshError) {
				if (store) {
					store.dispatch(logoutUser()); // ✅ Dispatch the logout action
				}
				jwService.cleanToken();
				return Promise.reject(refreshError);
			}
		}
		// For any other errors, just reject the promise
		return Promise.reject(error);
	}
);

export default api;
